import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingCart, ShoppingBag, ArrowRight, Package, Truck, Shield, CreditCard, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

export const Cart = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, cartTotal } = useCart();
  const { user } = useAuth();
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchProducts();
  }, [user, cart]);

  const fetchProducts = async () => {
    if (!cart.items || cart.items.length === 0) {
      setLoading(false);
      return;
    }
    try {
      const productIds = cart.items.map(item => item.product_id);
      const responses = await Promise.all(
        productIds.map(id => axios.get(`${API}/products/${id}`))
      );
      const productsMap = {};
      responses.forEach(res => {
        productsMap[res.data.id] = res.data;
      });
      setProducts(productsMap);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      await updateQuantity(productId, newQuantity);
    } catch (error) {
      toast.error('Erro ao atualizar quantidade');
    }
  };

  const handleRemove = async (productId) => {
    try {
      await removeFromCart(productId);
      toast.success('Produto removido do carrinho');
    } catch (error) {
      toast.error('Erro ao remover produto');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-lg mx-auto text-center py-20">
            <div className="w-32 h-32 bg-slate-200 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-8">
              <ShoppingCart className="h-16 w-16 text-slate-400" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">Seu carrinho está vazio</h1>
            <p className="text-slate-600 dark:text-slate-300 mb-8">
              Parece que você ainda não adicionou nenhum produto ao carrinho. Explore nossa loja e encontre o que precisa!
            </p>
            <Button 
              size="lg" 
              onClick={() => navigate('/shop')} 
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 rounded-full px-8"
              data-testid="continue-shopping-btn"
            >
              <ShoppingBag className="mr-2 h-5 w-5" />
              Explorar Loja
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] pt-24 pb-12">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0f172a] via-blue-900 to-[#0f172a] py-12 mb-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center">
              <ShoppingCart className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">Meu Carrinho</h1>
              <p className="text-slate-300">{itemCount} {itemCount === 1 ? 'item' : 'itens'} no carrinho</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item, index) => {
              const product = products[item.product_id];
              if (!product) return null;
              return (
                <div 
                  key={item.product_id} 
                  className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg overflow-hidden border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-shadow"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <div className="flex flex-col sm:flex-row">
                    {/* Product Image */}
                    <div className="relative sm:w-48 h-48 sm:h-auto flex-shrink-0">
                      <img
                        src={product.images[0] || 'https://via.placeholder.com/200'}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 left-3 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm px-2 py-1 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-300">
                        #{index + 1}
                      </div>
                    </div>
                    
                    {/* Product Details */}
                    <div className="flex-1 p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-1 hover:text-cyan-600 cursor-pointer" onClick={() => navigate(`/product/${item.product_id}`)}>
                            {product.name}
                          </h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            Preço unitário: {formatCurrency(item.price)}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemove(item.product_id)}
                          className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                          data-testid={`remove-${item.product_id}`}
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                      
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        {/* Quantity Controls */}
                        <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-900 rounded-xl p-1">
                          <button
                            onClick={() => handleUpdateQuantity(item.product_id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            data-testid={`decrease-qty-${item.product_id}`}
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-12 text-center font-bold text-lg" data-testid={`qty-${item.product_id}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => handleUpdateQuantity(item.product_id, item.quantity + 1)}
                            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white dark:hover:bg-slate-700 transition-colors"
                            data-testid={`increase-qty-${item.product_id}`}
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                        
                        {/* Item Total */}
                        <div className="text-right">
                          <p className="text-sm text-slate-500 dark:text-slate-400">Subtotal</p>
                          <p className="text-xl font-bold text-cyan-600 dark:text-cyan-400">
                            {formatCurrency(item.price * item.quantity)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Continue Shopping */}
            <button 
              onClick={() => navigate('/shop')}
              className="w-full py-4 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-2xl text-slate-500 dark:text-slate-400 hover:border-cyan-500 hover:text-cyan-600 transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="h-5 w-5" />
              Adicionar mais produtos
            </button>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden sticky top-28">
              {/* Header */}
              <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  Resumo do Pedido
                </h2>
              </div>
              
              <div className="p-6">
                {/* Items Summary */}
                <div className="space-y-3 mb-6">
                  {cart.items.map(item => {
                    const product = products[item.product_id];
                    if (!product) return null;
                    return (
                      <div key={item.product_id} className="flex justify-between text-sm">
                        <span className="text-slate-600 dark:text-slate-400 truncate max-w-[60%]">
                          {product.name} x{item.quantity}
                        </span>
                        <span className="font-medium">{formatCurrency(item.price * item.quantity)}</span>
                      </div>
                    );
                  })}
                </div>
                
                <div className="border-t border-slate-200 dark:border-slate-700 pt-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Subtotal</span>
                    <span className="font-semibold" data-testid="subtotal">{formatCurrency(cartTotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Frete</span>
                    <span className="text-sm text-slate-500">Calcular no checkout</span>
                  </div>
                </div>
                
                <div className="border-t border-slate-200 dark:border-slate-700 mt-4 pt-4">
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-lg font-bold">Total</span>
                    <span className="text-2xl font-bold text-cyan-600 dark:text-cyan-400" data-testid="total">
                      {formatCurrency(cartTotal)}
                    </span>
                  </div>
                  
                  <Button 
                    className="w-full h-14 text-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 rounded-xl shadow-lg shadow-cyan-500/25" 
                    onClick={() => navigate('/checkout')} 
                    data-testid="checkout-btn"
                  >
                    Finalizar Compra
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
                
                {/* Trust Badges */}
                <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { icon: Shield, label: 'Compra Segura' },
                      { icon: Truck, label: 'Entrega Rápida' },
                      { icon: CreditCard, label: 'Parcelamento' },
                    ].map((badge, i) => (
                      <div key={i} className="text-center">
                        <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-xl flex items-center justify-center mx-auto mb-1">
                          <badge.icon className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{badge.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};